import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  User, Shield, Bell, Moon, Building2, Users, GitBranch,
  AlertTriangle, Plug, Database, FileCheck, ScrollText, CreditCard, Sliders
} from 'lucide-react';
import { toast } from 'sonner';

const settingsSections = [
  { id: 'profile', label: 'Organization Profile', icon: Building2 },
  { id: 'users', label: 'Users & Roles', icon: Users },
  { id: 'assignments', label: 'Assignments', icon: GitBranch },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'alerts', label: 'Alert Rules', icon: AlertTriangle },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'security', label: 'Data & Security', icon: Shield },
  { id: 'compliance', label: 'Compliance', icon: FileCheck },
  { id: 'audit', label: 'Audit Logs', icon: ScrollText },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'preferences', label: 'System Preferences', icon: Sliders },
];

const Settings = ({ user, onLogout }) => {
  const [activeSection, setActiveSection] = useState('profile');
  const [notifications, setNotifications] = useState({
    criticalMembers: true,
    dailySummary: true,
    weeklyDigest: true,
    systemUpdates: true,
    newEnrollment: false,
    smsAlerts: false,
  });
  const [darkMode, setDarkMode] = useState(false);
  const [timezone, setTimezone] = useState('EST');

  const handleSave = () => {
    toast.success('Settings saved successfully!');
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <Card data-testid="profile-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4 text-purple-700" />
                Organization Profile
              </CardTitle>
              <p className="text-xs text-gray-500">Manage your organization details and program information</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Organization</Label>
                  <p className="text-sm font-medium">HealthLink360 Pilot Program</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Region</Label>
                  <p className="text-sm font-medium">Atlanta, GA</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Program Type</Label>
                  <p className="text-sm font-medium">Chronic Care Management</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Account Status</Label>
                  <p className="text-sm font-medium text-green-700">Active</p>
                </div>
              </div>
              <Button variant="outline" size="sm" data-testid="edit-org-btn">Edit Organization</Button>
            </CardContent>
          </Card>
        );

      case 'users':
        return (
          <Card data-testid="users-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-700" />
                Users & Roles
              </CardTitle>
              <p className="text-xs text-gray-500">Manage team members and role-based access permissions</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { name: user?.name || 'Admin User', email: user?.email || 'admin@healthlink360.com', role: user?.role || 'Administrator' },
                { name: 'Dr. Sarah Chen', email: 'doctor@healthlink360.com', role: 'Physician' },
                { name: 'Maria Rodriguez', email: 'nurse@healthlink360.com', role: 'Nurse' },
              ].map((u, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-purple-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{u.name}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </div>
                  </div>
                  <span className="text-xs font-medium px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full">{u.role}</span>
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="invite-user-btn">Invite User</Button>
            </CardContent>
          </Card>
        );

      case 'assignments':
        return (
          <Card data-testid="assignments-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-purple-700" />
                Assignments
              </CardTitle>
              <p className="text-xs text-gray-500">Configure member-to-provider assignment rules and load balancing</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-sm font-medium">Auto-Assignment</p>
                  <p className="text-xs text-gray-500 mt-1">New members are automatically assigned based on provider capacity and specialty</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-sm font-medium">Caseload Limits</p>
                  <p className="text-xs text-gray-500 mt-1">Maximum 50 members per provider. 3 providers currently below threshold.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'notifications':
        return (
          <Card data-testid="notifications-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="w-4 h-4 text-purple-700" />
                Notification Preferences
              </CardTitle>
              <p className="text-xs text-gray-500">Configure how and when you receive alerts</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'criticalMembers', label: 'Critical Member Alerts', desc: 'Immediate alerts for high-risk members' },
                { key: 'dailySummary', label: 'Daily Summary Report', desc: 'Morning digest of member status updates' },
                { key: 'weeklyDigest', label: 'Weekly Analytics Digest', desc: 'Weekly trends and insights' },
                { key: 'systemUpdates', label: 'System Updates', desc: 'Platform maintenance and feature updates' },
                { key: 'newEnrollment', label: 'New Member Enrollment', desc: 'Notify when new members join' },
                { key: 'smsAlerts', label: 'SMS Alerts for High-Risk', desc: 'Text messages for urgent cases' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{item.label}</p>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                  <Switch
                    checked={notifications[item.key]}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, [item.key]: checked })}
                    data-testid={`toggle-${item.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        );

      case 'alerts':
        return (
          <Card data-testid="alerts-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-purple-700" />
                Alert Rules
              </CardTitle>
              <p className="text-xs text-gray-500">Define thresholds and conditions that trigger clinical alerts</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { rule: 'DTS drops below 60', severity: 'Critical', active: true },
                  { rule: 'No sync for 48+ hours', severity: 'High', active: true },
                  { rule: 'Mood score below 4 for 3+ days', severity: 'High', active: true },
                  { rule: 'Adherence below 50%', severity: 'Medium', active: true },
                  { rule: 'SpO2 below 92%', severity: 'Critical', active: true },
                ].map((alert, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div>
                      <p className="text-sm font-medium">{alert.rule}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        alert.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                        alert.severity === 'High' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>{alert.severity}</span>
                    </div>
                    <Switch checked={alert.active} data-testid={`alert-rule-${i}`} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case 'integrations':
        return (
          <Card data-testid="integrations-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Plug className="w-4 h-4 text-purple-700" />
                Integrations
              </CardTitle>
              <p className="text-xs text-gray-500">Connected services and data sources</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { name: 'Wearable Devices', status: 'Connected', desc: 'Apple Watch, Fitbit, Oura Ring' },
                  { name: 'EHR System', status: 'Pending', desc: 'Epic integration awaiting approval' },
                  { name: 'SMS Gateway', status: 'Connected', desc: 'Twilio for nudge delivery' },
                  { name: 'Analytics Export', status: 'Available', desc: 'CSV and API export ready' },
                ].map((int, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div>
                      <p className="text-sm font-medium">{int.name}</p>
                      <p className="text-xs text-gray-500">{int.desc}</p>
                    </div>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      int.status === 'Connected' ? 'bg-green-100 text-green-700' :
                      int.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>{int.status}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case 'security':
        return (
          <Card data-testid="security-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4 text-purple-700" />
                Data & Security
              </CardTitle>
              <p className="text-xs text-gray-500">Authentication, encryption, and access controls</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm font-medium">Two-Factor Authentication</p>
                <p className="text-xs text-green-600 font-medium mt-0.5">Enabled via Authenticator App</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm font-medium">Last Password Change</p>
                <p className="text-xs text-gray-500 mt-0.5">32 days ago</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm font-medium">Active Sessions</p>
                <p className="text-xs text-gray-500 mt-0.5">2 (Web Portal, Mobile App)</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm font-medium">Data Encryption</p>
                <p className="text-xs text-green-600 font-medium mt-0.5">AES-256 at rest, TLS 1.3 in transit</p>
              </div>
              <div className="flex space-x-2 mt-4">
                <Button variant="outline" size="sm" data-testid="change-password-btn">Change Password</Button>
                <Button variant="outline" size="sm" data-testid="view-security-log-btn">View Security Log</Button>
              </div>
            </CardContent>
          </Card>
        );

      case 'compliance':
        return (
          <Card data-testid="compliance-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-purple-700" />
                Compliance
              </CardTitle>
              <p className="text-xs text-gray-500">Regulatory and standards compliance status</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { standard: 'HIPAA', status: 'Compliant', lastAudit: 'Dec 2025' },
                  { standard: 'SOC 2 Type II', status: 'Compliant', lastAudit: 'Nov 2025' },
                  { standard: 'HITRUST', status: 'In Progress', lastAudit: 'Pending' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div>
                      <p className="text-sm font-medium">{item.standard}</p>
                      <p className="text-xs text-gray-500">Last audit: {item.lastAudit}</p>
                    </div>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      item.status === 'Compliant' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>{item.status}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case 'audit':
        return (
          <Card data-testid="audit-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ScrollText className="w-4 h-4 text-purple-700" />
                Audit Logs
              </CardTitle>
              <p className="text-xs text-gray-500">Track all administrative actions and data access events</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { action: 'Login', user: 'admin@healthlink360.com', time: '2 min ago' },
                  { action: 'Viewed member profile', user: 'doctor@healthlink360.com', time: '15 min ago' },
                  { action: 'Updated alert rules', user: 'admin@healthlink360.com', time: '1 hour ago' },
                  { action: 'Exported analytics report', user: 'admin@healthlink360.com', time: '3 hours ago' },
                  { action: 'Sent batch nudge (12 members)', user: 'nurse@healthlink360.com', time: '5 hours ago' },
                ].map((log, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded border border-gray-100 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-700">{log.action}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{log.user}</span>
                      <span>{log.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );

      case 'billing':
        return (
          <Card data-testid="billing-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-purple-700" />
                Billing
              </CardTitle>
              <p className="text-xs text-gray-500">Subscription and usage details</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm font-medium">Current Plan</p>
                <p className="text-xs text-purple-700 font-medium mt-0.5">Enterprise - Pilot Program</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm font-medium">Member Capacity</p>
                <p className="text-xs text-gray-500 mt-0.5">100 of 500 seats used</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-sm font-medium">Billing Cycle</p>
                <p className="text-xs text-gray-500 mt-0.5">Annual (Renews March 2026)</p>
              </div>
            </CardContent>
          </Card>
        );

      case 'preferences':
        return (
          <Card data-testid="preferences-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sliders className="w-4 h-4 text-purple-700" />
                System Preferences
              </CardTitle>
              <p className="text-xs text-gray-500">Display and application settings</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Dark Mode</p>
                  <p className="text-xs text-gray-500">Switch to dark theme</p>
                </div>
                <Switch checked={darkMode} onCheckedChange={setDarkMode} data-testid="dark-mode-toggle" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Time Zone</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger data-testid="timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EST">Eastern Time (US & Canada)</SelectItem>
                    <SelectItem value="CST">Central Time (US & Canada)</SelectItem>
                    <SelectItem value="MST">Mountain Time (US & Canada)</SelectItem>
                    <SelectItem value="PST">Pacific Time (US & Canada)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Default Member Sort</Label>
                <Select defaultValue="risk-high">
                  <SelectTrigger data-testid="default-sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="risk-high">Risk (High First)</SelectItem>
                    <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                    <SelectItem value="dts-low">DTS (Low First)</SelectItem>
                    <SelectItem value="last-sync">Last Sync</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="settings-title">
          Admin Settings
        </h1>
        <p className="text-gray-600 mt-1">Manage your organization, team, and system configuration</p>
      </div>

      <div className="flex gap-6">
        {/* Settings Nav */}
        <div className="hidden md:block w-56 shrink-0">
          <nav className="space-y-0.5">
            {settingsSections.map((section) => {
              const Icon = section.icon;
              const active = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                    active ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`settings-nav-${section.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {section.label}
                </button>
              );
            })}
          </nav>

          <div className="mt-6 pt-4 border-t border-gray-200 space-y-2">
            <Button
              onClick={handleSave}
              className="w-full bg-purple-700 hover:bg-purple-800"
              size="sm"
              data-testid="save-settings-btn"
            >
              Save All Changes
            </Button>
            <Button
              variant="destructive"
              onClick={onLogout}
              className="w-full"
              size="sm"
              data-testid="logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Section Content */}
        <div className="flex-1 min-w-0">
          {/* Mobile section selector */}
          <div className="md:hidden mb-4">
            <Select value={activeSection} onValueChange={setActiveSection}>
              <SelectTrigger data-testid="mobile-section-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {settingsSections.map((section) => (
                  <SelectItem key={section.id} value={section.id}>{section.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {renderSectionContent()}

          {/* Mobile action buttons */}
          <div className="md:hidden flex gap-2 mt-4">
            <Button onClick={handleSave} className="flex-1 bg-purple-700 hover:bg-purple-800" size="sm" data-testid="save-settings-mobile-btn">
              Save All Changes
            </Button>
            <Button variant="destructive" onClick={onLogout} size="sm" data-testid="logout-mobile-btn">
              Logout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
